import React, { useEffect, useState } from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Chip } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { Student } from "../types/StudentModel";
import { getStudents } from "../services/studentService";
import EmailAvatar from "./EmailAvatar";
import { DynamicTableListTemplate } from "./shared/DynamicTableListTemplate";
import { useHandleError } from "../hooks/useHandleError";

const StudentList: React.FC = () => {
  const handleError = useHandleError();
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const hasFetchedRef = React.useRef(false);

  const columns: GridColDef[] = [
    {
      field: "isActive",
      headerName: "Status",
      width: 90,
      renderCell: (params) => {
        const color = params.value === true ? "success" : "default";
        const text = params.value === true ? "ACTIVE" : "DISABLED";
        return (
          <Chip
            style={{ fontSize: ".7rem" }}
            label={text}
            size="small"
            color={color}
          />
        );
      },
    },
    {
      field: "name",
      headerName: "Name",
      width: 200,
      valueGetter: (params) =>
        `${params.row?.firstName || ""} ${params.row?.lastName || ""}`.trim(),
    },
    { field: "email", headerName: "Email", width: 250 },
    { field: "currentSchool", headerName: "School", width: 200 },
    {
      field: "city",
      headerName: "City",
      width: 120,
    },
    {
      field: "country",
      headerName: "Country",
      width: 120,
    },
    {
      field: "myClassroom",
      headerName: "Program",
      width: 140,
      valueGetter: (params) =>
        params.row?.myClassroom?.programName || "Not enrolled",
      renderCell: (params) => {
        const program = params.value;
        if (program === "Not enrolled") {
          return (
            <Chip
              style={{ fontSize: ".7rem" }}
              label={program}
              size="small"
              color="default"
            />
          );
        }
        const colorMap: Record<string, string> = {
          "EAP YELLOW": "#f2b443",
          "EAP GREEN": "#78c0a1",
          "EAP ACCESS": "#34c1d6",
        };
        return (
          <Chip
            style={{
              fontSize: ".7rem",
              backgroundColor: colorMap[program] || "#ccc",
            }}
            label={program}
            size="small"
          />
        );
      },
    },
    {
      field: "placementTest",
      headerName: "Placement",
      width: 120,
      valueGetter: (params) => {
        const result = params.row?.placementTest?.result;
        return result?.programName || "Not taken";
      },
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return "";
        const updatedBy = params.row["updatedBy"];
        const date = new Date(params.value);
        const formattedDate = date.toLocaleDateString("en-AU");
        return (
          <div
            style={{
              display: "flex",
              gap: ".25rem",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{formattedDate}</span>
            <EmailAvatar email={updatedBy} />
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchStudents();
      hasFetchedRef.current = true;
    }
  }, []);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await getStudents();
      setStudents(response.data);
    } catch (err: any) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DynamicTableListTemplate<Student>
      columns={columns}
      data={students}
      loading={loading}
      entityLabel="Student"
      dialogTitle="Student Details"
      onConfirmDelete={() => {}}
      entityToDelete={null}
    />
  );
};

export default StudentList;
